/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Map as MapIcon, Camera, MapPin, Eye, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker as LeafletMarker, Popup } from 'react-leaflet';

// Type workaround for react-leaflet
const Marker = LeafletMarker as any;
import 'leaflet/dist/leaflet.css';
import L, { LatLngExpression, Icon } from 'leaflet';
import { fetchUserCameras, CameraData, GO2RTC_URL } from '../lib/authFetch';
import { useLiveStreamName } from '@/hooks/useLiveStreamName';

// CSS específico para o mapa
const mapStyles = `
  .leaflet-container {
    z-index: 1 !important;
    position: relative !important;
  }
  .leaflet-control-container {
    z-index: 2 !important;
  }
  .leaflet-popup {
    z-index: 3 !important;
  }
`;

// Ícone usando L.Icon padrão - SEM NENHUM CSS EXTRA
const cameraIconOnline: Icon = new L.Icon({
  iconUrl: '/marker-totem.jpg',
  iconSize: [40, 60],
  iconAnchor: [20, 60],
  popupAnchor: [0, -55],
});

const cameraIconOffline: Icon = new L.Icon({
  iconUrl: '/marker-totem.jpg',
  iconSize: [40, 60],
  iconAnchor: [20, 60],
  popupAnchor: [0, -55],
});

const InteractiveMap = () => {
  const [cameras, setCameras] = useState<CameraData[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<CameraData | null>(null);
  const [showPlayer, setShowPlayer] = useState(false);
  const liveStreamName = useLiveStreamName(
    showPlayer && selectedCamera ? (selectedCamera.stream_id || `camera_${selectedCamera.id}`) : null
  );

  useEffect(() => {
    const loadCameras = async () => {
      try {
        // Dashboard do cliente: sempre lista filtrada por permissão
        const data = await fetchUserCameras();
        setCameras(data);
      } catch (e) {
        // erro ao buscar câmeras
      }
    };
    loadCameras();
  }, []);

  const handleAssistir = (camera: CameraData) => {
    setSelectedCamera(camera);
    setShowPlayer(true);
  };

  const closePlayer = () => {
    setShowPlayer(false);
    setSelectedCamera(null);
  };

  // Cálculo dos indicadores reais
  const onlineCount = cameras.filter(cam => cam.status === 'online').length;
  const offlineCount = cameras.filter(cam => cam.status === 'offline').length;
  // Visualizações: placeholder, pois não há campo no backend
  const visualizacoes = cameras.length * 10; // Exemplo fictício
  // Estados distintos
  const estados = Array.from(new Set(cameras.map(cam => {
    // Extrai a sigla do estado do final do campo localizacao, se existir
    const match = cam.localizacao?.match(/([A-Z]{2})$/);
    return match ? match[1] : null;
  }).filter(Boolean)));
  const estadosCount = estados.length;

  return (
    <div className="space-y-6">
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <MapIcon className="w-5 h-5 text-protec-gold" />
              Mapa Interativo
            </CardTitle>
            <Badge className="bg-green-500/20 text-green-400 text-sm">
              <Camera className="w-4 h-4 mr-1" />
              {onlineCount} Online
            </Badge>
          </div>
          <CardDescription className="text-gray-300">
            Visualize todas as câmeras ativas em tempo real com geolocalização precisa
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative">
            {/* CSS específico para o mapa */}
            <style dangerouslySetInnerHTML={{ __html: mapStyles }} />
            
            {/* Mapa real com react-leaflet */}
            <div className="rounded-lg border border-protec-gold/20 overflow-hidden relative" style={{ height: 600 }}>
              {/* @ts-ignore - react-leaflet types issue */}
              <MapContainer center={[-14.235, -51.9253] as LatLngExpression} zoom={4} style={{ height: '100%', width: '100%' }} className="w-full h-full">
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {cameras.filter(cam => cam.latitude && cam.longitude).map(cam => (
                  <Marker
                    key={cam.id}
                    position={[Number(cam.latitude), Number(cam.longitude)] as LatLngExpression}
                    icon={cam.status === 'online' ? cameraIconOnline : cameraIconOffline}
                  >
                    <Popup>
                      <div className="space-y-1">
                        <strong>{cam.nome}</strong><br/>
                        {cam.localizacao}<br/>
                        Status: <span className={cam.status === 'online' ? 'text-green-500' : 'text-red-500'}>{cam.status}</span><br/>
                        <Button 
                          size="sm" 
                          className="mt-2 bg-protec-gold text-black w-full hover:bg-protec-gold-light"
                          onClick={() => handleAssistir(cam)}
                          disabled={cam.status !== 'online'}
                        >
                          {cam.status === 'online' ? 'Assistir' : 'Offline'}
                        </Button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
            
            {/* Legend */}
            <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm rounded-lg p-3 border border-protec-gold/20 z-10">
              <h4 className="text-white text-sm font-semibold mb-2">Legenda</h4>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-gray-300">Online</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <span className="text-gray-300">Offline</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <span className="text-gray-300">Manutenção</span>
                </div>
              </div>
            </div>
          </div>
          
        </CardContent>
      </Card>

      {/* Modal de Player */}
      {showPlayer && selectedCamera && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/90 backdrop-blur-sm"
            onClick={closePlayer}
          />
          
          {/* Modal Content */}
          <div className="relative w-full max-w-4xl max-h-[90vh] bg-protec-dark border border-protec-gold/30 rounded-xl overflow-hidden shadow-2xl z-10">
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-protec-gold/20 bg-gradient-to-r from-protec-dark-light to-protec-dark">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${selectedCamera.status === 'online' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                  <Camera className={`w-5 h-5 ${selectedCamera.status === 'online' ? 'text-green-400' : 'text-red-400'}`} />
                </div>
                <div>
                  <h2 className="text-white font-bold">{selectedCamera.nome}</h2>
                  <p className="text-gray-400 text-sm">{selectedCamera.localizacao || 'Sem localização'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Badge className={`${selectedCamera.status === 'online' ? 'bg-green-500' : 'bg-red-500'} text-white`}>
                  ● {selectedCamera.status === 'online' ? 'Online' : 'Offline'}
                </Badge>
                <button
                  className="text-gray-400 hover:text-white transition-colors p-2"
                  onClick={closePlayer}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Área de Vídeo */}
            <div className="bg-black relative aspect-video">
              {liveStreamName && <iframe
                src={`${GO2RTC_URL}/stream.html?src=${liveStreamName}&mode=mse,hls,mjpeg&video`}
                className="w-full h-full border-0"
                allow="autoplay"
                style={{ background: 'black' }}
              />}
              {/* Indicador Ao Vivo */}
              <div className="absolute top-3 left-3 bg-red-600 text-white px-2 py-1 rounded text-xs flex items-center gap-1.5 shadow-lg">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                AO VIVO
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveMap;
